/**
 * API Client - Base fetch wrapper
 * Handles API requests with proper typing, error handling, and App Bridge session tokens
 */

import type { ApiResponse } from '../../../../shared/types';
import { getSessionToken } from './app-bridge';

/**
 * Backend API base URL per app, keyed by the frontend origin (hostname).
 *
 * A single frontend build serves multiple Shopify apps, one per origin. Each
 * app's session tokens can only be validated by the backend running that app's
 * SHOPIFY_API_KEY/SECRET, so requests must go to the matching backend. The
 * origin is the source of truth for "which app am I" (see index.html); anything
 * not listed here falls back to VITE_API_BASE_URL. Add a line per app.
 */
const API_BASE_URL_BY_HOST: Record<string, string> = {
  'shop-app-daily-hi.stickycards.co': 'https://shop-daily-hi.stickycards.co', // Daily Hi
  'shop-app-dryptides.stickycards.co': 'https://shop-dryptides.stickycards.co', // Dryptides
  'shop-app-gazz.stickycards.co': 'https://shop-gazz.stickycards.co', // Gazz
};

function getApiBaseUrl(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return API_BASE_URL_BY_HOST[host] || import.meta.env.VITE_API_BASE_URL || '';
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Base fetch function with type safety and App Bridge session tokens
 */
export async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<ApiResponse<T>> {
  const url = `${getApiBaseUrl()}${endpoint}`;
  
  // Get session token from App Bridge (if available)
  // This will be null if App Bridge is not initialized (e.g., running outside Shopify admin)
  const sessionToken = await getSessionToken();
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      // Add session token in Authorization header if available
      ...(sessionToken && {
        'Authorization': `Bearer ${sessionToken}`,
      }),
      ...options?.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: response.statusText,
      }));
      
      throw new ApiError(
        errorData.message || `API request failed: ${response.statusText}`,
        response.status,
        errorData,
      );
    }

    const data: ApiResponse<T> = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      0,
      error,
    );
  }
}

/**
 * GET request helper
 */
export function apiGet<T>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<ApiResponse<T>> {
  const queryString = params
    ? '?' + new URLSearchParams(
        Object.entries(params).reduce(
          (acc, [key, value]) => ({ ...acc, [key]: String(value) }),
          {} as Record<string, string>,
        ),
      ).toString()
    : '';
  
  return apiFetch<T>(`${endpoint}${queryString}`, {
    method: 'GET',
  });
}

/**
 * POST request helper
 */
export function apiPost<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request helper
 */
export function apiPut<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request helper
 */
export function apiDelete<T>(endpoint: string): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    method: 'DELETE',
  });
}

